/**
 * MyPortfolioPage — profil użytkownika + lista członków rodziny z kwotami wpłaconymi.
 * Gamification (streaki, diamenty) — Faza 6.5 (V2.5).
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Icons } from '../components/Icons';
import { useAuthStore } from '../features/auth/authStore';
import { useTransactionsStore } from '../features/transactions/transactionsStore';
import { useGoalsStore, goalSaved } from '../features/goals/goalsStore';
import { useBudgetsStore } from '../features/budgets/budgetsStore';
import { formatCurrency, getInitials } from '../lib/utils';
import { DailyClaimCard } from '../components/gamification/DailyClaimCard';

export function MyPortfolioPage() {
  const user = useAuthStore((s) => s.user);
  const family = useAuthStore((s) => s.family);
  const transactions = useTransactionsStore((s) => s.transactions);
  const goals = useGoalsStore((s) => s.goals);
  const budgets = useBudgetsStore((s) => s.budgets);

  const myStats = useMemo(() => {
    if (!user) return { income: 0, expense: 0, txCount: 0 };
    let income = 0;
    let expense = 0;
    let txCount = 0;
    for (const t of transactions) {
      if (t.userId !== user.uid) continue;
      txCount++;
      if (t.type === 'income') income += t.amount;
      else if (t.type === 'expense') expense += t.amount;
    }
    return { income, expense, txCount };
  }, [transactions, user]);

  const ranking = useMemo(() => {
    const map = new Map<string, { income: number; expense: number; txCount: number }>();
    for (const t of transactions) {
      const cur = map.get(t.userId) ?? { income: 0, expense: 0, txCount: 0 };
      cur.txCount++;
      if (t.type === 'income') cur.income += t.amount;
      else if (t.type === 'expense') cur.expense += t.amount;
      map.set(t.userId, cur);
    }
    const members = Object.entries(family?.members ?? {}).map(([uid, m]) => ({
      uid,
      name: m.name,
      ...(map.get(uid) ?? { income: 0, expense: 0, txCount: 0 }),
    }));
    return members.sort((a, b) => b.income - a.income);
  }, [transactions, family]);

  const totalGoalSaved = goals.reduce((s, g) => s + goalSaved(g), 0);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-display font-bold text-slate-900 dark:text-white">Mój profil</h1>
        <Link
          to="/settings"
          aria-label="Ustawienia"
          className="w-10 h-10 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          <Icons.Settings size={20} />
        </Link>
      </div>

      <DailyClaimCard />

      {/* Profil card */}
      <div className="bg-gradient-to-br from-sky-500 to-emerald-500 rounded-card shadow-card p-5 text-white">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur flex items-center justify-center font-display font-bold text-2xl">
            {getInitials(user?.name)}
          </div>
          <div>
            <p className="text-xl font-bold">{user?.name ?? '—'}</p>
            <p className="text-sm opacity-90">{user?.email ?? '—'}</p>
            <p className="text-xs opacity-75 mt-0.5">Rodzina: {family?.name ?? '—'}</p>
          </div>
        </div>
      </div>

      {/* Moje statystyki */}
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Transakcje" value={String(myStats.txCount)} icon={<Icons.Receipt size={14} />} tone="info" />
        <Stat label="Przychody" value={formatCurrency(myStats.income)} icon={<Icons.TrendingUp size={14} />} tone="income" />
        <Stat label="Wydatki" value={formatCurrency(myStats.expense)} icon={<Icons.TrendingDown size={14} />} tone="expense" />
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-2 gap-2">
        <Link to="/goals" className="bg-white dark:bg-slate-800 rounded-card border border-slate-100 dark:border-slate-700 shadow-card p-3 flex items-center gap-3 hover:border-sky-300 transition">
          <div className="w-9 h-9 rounded-full bg-violet-100 dark:bg-violet-500/15 flex items-center justify-center text-violet-600 dark:text-violet-400">
            <Icons.PiggyBank size={18} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">Cele</p>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              {formatCurrency(totalGoalSaved)}
            </p>
          </div>
        </Link>
        <Link to="/budgets" className="bg-white dark:bg-slate-800 rounded-card border border-slate-100 dark:border-slate-700 shadow-card p-3 flex items-center gap-3 hover:border-sky-300 transition">
          <div className="w-9 h-9 rounded-full bg-amber-100 dark:bg-amber-500/15 flex items-center justify-center text-amber-600 dark:text-amber-400">
            <Icons.ChartPie size={18} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">Budżety</p>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              {budgets.length === 0 ? 'Skonfiguruj' : `${budgets.length} aktywnych`}
            </p>
          </div>
        </Link>
      </div>

      {/* Ranking rodziny */}
      <div className="bg-white dark:bg-slate-800 rounded-card border border-slate-100 dark:border-slate-700 shadow-card p-4">
        <h2 className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200 mb-3">
          <Icons.Award size={16} className="text-amber-500" />
          Ranking rodziny
        </h2>
        <ul className="space-y-2">
          {ranking.map((m, i) => {
            const isMe = m.uid === user?.uid;
            return (
              <li
                key={m.uid}
                className={`flex items-center gap-3 p-2.5 rounded-xl ${
                  isMe ? 'bg-sky-50 dark:bg-sky-500/10 border border-sky-200 dark:border-sky-500/30' : ''
                }`}
              >
                <span className="w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-500 dark:text-slate-300">
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                    {m.name} {isMe && <span className="text-[10px] text-sky-600 dark:text-sky-300 font-bold ml-1">TY</span>}
                  </p>
                  <p className="text-[11px] text-slate-400">{m.txCount} transakcji</p>
                </div>
                <span className="text-sm font-bold tabular-nums text-emerald-600 dark:text-emerald-300">
                  +{formatCurrency(m.income)}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function Stat({ label, value, icon, tone }: { label: string; value: string; icon: React.ReactNode; tone: 'income' | 'expense' | 'info' }) {
  const colorClass = tone === 'income' ? 'text-emerald-600 dark:text-emerald-300' : tone === 'expense' ? 'text-rose-600 dark:text-rose-300' : 'text-sky-600 dark:text-sky-300';
  return (
    <div className="bg-white dark:bg-slate-800 rounded-card border border-slate-100 dark:border-slate-700 shadow-card p-3">
      <div className={`flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold mb-1 ${colorClass}`}>
        {icon}
        <span>{label}</span>
      </div>
      <p className="text-sm font-bold tabular-nums text-slate-900 dark:text-white truncate">{value}</p>
    </div>
  );
}
